import React, { useCallback, useEffect, useState } from "react"

import aula, { EstadoDePases } from "../../services/v2/aula.service"
import "./accesoAClases.css"

/*
	Acceso a clases (aula virtual, Fase 2): vive en la ficha del alumno y SOLO la ve quien puede dar el pase
	de 48 h (Caja, Servicios Académicos / Dirección Académica, Super Admin): el servidor responde 403 a los
	demás y aquí no se dibuja nada. Un pago reportado y en revisión no desbloquea la entrada; solo el pago
	aplicado o este pase. Queda registrado quién lo dio, cuándo y por qué.
*/
const AccesoAClases: React.FC<{ studentId: number }> = ({ studentId }) => {
	const [estado, setEstado] = useState<EstadoDePases | null>(null)
	const [abierto, setAbierto] = useState<boolean>(false)
	const [motivo, setMotivo] = useState<string>("")
	const [enviando, setEnviando] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		let vivo = true
		setEstado(null); setAbierto(false); setMotivo(""); setError(null)
		aula.passState(studentId).then((r) => { if (vivo) setEstado(r.data) }).catch(() => { /* sin permiso: no se muestra */ })
		return () => { vivo = false }
	}, [studentId])

	const dar = useCallback(async () => {
		setError(null)
		setEnviando(true)
		try {
			const r = await aula.givePass(studentId, motivo)
			setEstado(r.data); setAbierto(false); setMotivo("")
		} catch (e: any) {
			setError(e?.response?.data?.message || "No se pudo dar el pase. Intenta otra vez.")
		} finally { setEnviando(false) }
	}, [studentId, motivo])

	if (estado === null || !estado.canGive) return null

	const vencido = estado.overdue.map((m) => m.symbol + m.amount.toLocaleString("en-US", { minimumFractionDigits: 2 }) + " " + m.currency).join(" · ")
	return (
		<div className="aaPase">
			<h3 className="aaTitulo">Acceso A Clases</h3>
			<div className="aaEstado">
				{estado.pass
					? <span className="aaVigente">Pase vigente hasta el {estado.pass.untilLabel}</span>
					: <span className="aaSin">Sin pase</span>}
				{estado.hasOverdue
					? <span className="aaVencido">Vencido {vencido}{estado.oldestOverdue ? " desde el " + estado.oldestOverdue.dueLabel : ""}</span>
					: <span className="aaSin">Sin saldo vencido</span>}
			</div>
			{estado.pass ? <div className="aaSin">{estado.pass.by ? estado.pass.by + " · " : ""}{estado.pass.reason}</div> : null}

			{abierto ? (
				<div className="aaForma">
					<label htmlFor="aaMotivo" className="aaEtiqueta">Motivo</label>
					<textarea id="aaMotivo" className="aaMotivo" rows={3} maxLength={200} value={motivo} autoFocus
						onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. Pagó por transferencia, comprobante en Caja." />
					{error ? <div className="aaError" role="alert">{error}</div> : null}
					<div className="aaAcciones">
						<button type="button" className="aaBoton" disabled={enviando || motivo.trim().length < 3} onClick={dar}>
							Dar Pase De {estado.hours} Horas
						</button>
						<button type="button" className="aaBotonSec" disabled={enviando} onClick={() => { setAbierto(false); setError(null) }}>Cancelar</button>
					</div>
				</div>
			) : (
				<div className="aaAcciones">
					<button type="button" className="aaBoton" onClick={() => setAbierto(true)}>Dar Pase De {estado.hours} Horas</button>
				</div>
			)}

			{estado.history.filter((p) => p.id !== estado.pass?.id).length > 0 ? (
				<div className="aaHistorial">
					<span className="aaEtiqueta">Pases Anteriores</span>
					{estado.history.filter((p) => p.id !== estado.pass?.id).map((p) => (
						<div className="aaFila" key={p.id}>
							<span className="aaFilaQuien">{p.givenLabel}{p.by ? " · " + p.by : ""}</span>
							<span className="aaFilaMotivo">{p.reason}</span>
						</div>
					))}
				</div>
			) : null}
		</div>
	)
}

export default AccesoAClases
